// 稽核（SPEC #vms-web-817 AUDIT-01..03；VMS UX 評審 2026-10-13 P1-9）：org 內所有管理動作，append-only。
// 篩選：日期範圍（今天｜昨天｜過去 7 天（預設）｜自訂）、動作中文分組（組內才列單項譯名）、操作者、全文；往前翻頁用 next_cursor、到範圍起點停；
// 每列一行白話摘要（操作者顯示名，查不到「已離開的成員」），展開看白話欄位、原始 JSON 收在「技術細節」；CSV 匯出（AUDIT-02）。
// 設備詳細「進階 › 稽核」用同一個清單（AuditRows / useAuditNames）。
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { kinioFetchResponse } from '@kinio/web-sdk';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { useApp } from '../../lib/AppContext';
import { hasOrgPerm, orgApi, type AuditEvent, type MyOrg } from '../../lib/planeApi';
import { OrgGate, relevantOrgs } from '../shell/OrgGate';
import { sinceStamp, stamp } from '../../lib/timefmt';
import { refFullName } from '../../lib/devices';
import { actionKey, AUDIT_GROUPS, auditFields, auditGroupOf, auditReplayStarted, auditSummary, auditWatched, collapseAudit, rangeOf, rowMatchesAction, WATCH_ACTIONS, type AuditGroup, type AuditNames, type AuditRange, type AuditRowData } from '../../lib/auditFmt';
import { Link, useSearchParams } from 'react-router-dom';
import { memberName } from '../people/MembersTab';

export function AuditPage() {
  const { t } = useTranslation();
  const { orgs, homes } = useScope();
  // 稽核只記在設備所屬的組織、一次看一個組織（SPEC #device-permissions-audit）；組織政策這次登入不符的不列
  const allowed = relevantOrgs(orgs, homes).filter((o) => !o.notice && hasOrgPerm(o, 'org.audit'));
  return (
    <div className="page">
      <h1 className="page-title">{t('nav.audit')}</h1>
      <OrgGate candidates={allowed} empty={<p className="card muted">{t('audit.pick_org')}</p>}>
        {(current) => <AuditLog key={current.org_id} org={current} />}
      </OrgGate>
    </div>
  );
}

const ACTIONS = [
  'org.create',
  'site.create',
  'site.update',
  'home.create',
  'home.member.add',
  'device.location',
  'grant.create',
  'grant.refresh',
  'grant.revoke',
  'role.create',
  'role.update',
  'member.update',
  'member.invite',
  'member.invite_revoke',
  'member.join',
  'member.role',
  'member.leave',
  'member.remove',
  'home.owner.handover',
  'home.owner.takeover',
  'home.unassigned',
  'org.security_policy',
  'org.owner_transfer',
  'org.delete',
  'share.create',
  'share.request',
  'share.request_cancel',
  'share.accept',
  'share.shrink',
  'share.revoke',
  'share.end',
  'share.handover',
  'share.grantee_removed',
  'member.service_roles',
  'member.duty_mode',
  'billing.checkout',
  'billing.seat_assign',
  'billing.seat_unassign',
  'ai.consent_grant',
  'ai.consent_exclusions',
  'ai.consent_revoke',
  'ai.usage_threshold',
];

/** 名稱查表：成員（組織成員清單）、設備（我的設備，通道寫完整名）、家戶（範圍裡的家戶）、節點（組織結構）。 */
export function useAuditNames(org: MyOrg): AuditNames {
  const { devices, homes } = useScope();
  const me = useApp().session?.user;
  const members = useQuery({ queryKey: ['org-members', org.org_id], queryFn: () => orgApi.members(org).catch(() => []), staleTime: 60_000 });
  const sites = useQuery({ queryKey: ['org-sites', org.org_id], queryFn: () => orgApi.sites(org).catch(() => []), staleTime: 60_000 });
  return useMemo(() => {
    const who = new Map((members.data ?? []).map((m) => [m.user_id, memberName(m)]));
    if (me?.user_id && !who.has(me.user_id)) who.set(me.user_id, me.display_name || me.email || '');
    const dev = new Map<string, string>();
    for (const d of devices) {
      dev.set(d.device_ref, d.display_name || d.model || '');
      for (const c of d.children ?? []) dev.set(c.device_ref, refFullName(c.device_ref, c.display_name ?? undefined, [d, ...(d.children ?? [])]));
    }
    const home = new Map(homes.map((h) => [h.home_id, h.display_name ?? '']));
    const node = new Map((sites.data ?? []).map((x) => [x.site_id, x.name]));
    const get = (m: Map<string, string>) => (id: string) => m.get(id) || undefined;
    return { user: get(who), device: get(dev), home: get(home), node: get(node) };
  }, [members.data, sites.data, devices, homes, me]);
}

function AuditLog({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const [action, setAction] = useState(''); // '' | 'g:<group>' | <action>
  const [actor, setActor] = useState('');
  const [range, setRange] = useState<AuditRange>('7d');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [input, setInput] = useState('');
  const [text, setText] = useState('');
  // 從設備頁〔到稽核頁看全部〕來：?device=<ref> 只看這台（畫面寫名稱，不把 ID 放進搜尋框）
  const [sp, setSp] = useSearchParams();
  const device = sp.get('device') ?? '';
  const [csvErr, setCsvErr] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setText(input.trim()), 400);
    return () => clearTimeout(id);
  }, [input]);
  const seen = useQuery({
    queryKey: ['org-audit-actions', org.org_id],
    queryFn: () => orgApi.auditActions(org).catch(() => [] as string[]),
    staleTime: 60_000,
  });
  // 組內只列有譯名的單項（不列英文動作代碼）
  const grouped = useMemo(() => {
    const all = [...new Set([...ACTIONS, ...(seen.data ?? [])])].filter((a) => t(actionKey(a), { defaultValue: '' }));
    return AUDIT_GROUPS.map((g) => ({ g, items: all.filter((a) => auditGroupOf(a) === g).sort((x, y) => t(actionKey(x)).localeCompare(t(actionKey(y)))) }));
  }, [seen.data, t]);
  const exportCsv = async () => {
    setCsvErr(false);
    try {
      const res = await kinioFetchResponse(orgApi.auditCsvUrl(org));
      const a = document.createElement('a');
      a.href = URL.createObjectURL(await res.blob());
      a.download = `audit-${org.org_id}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
    } catch {
      setCsvErr(true);
    }
  };
  const names = useAuditNames(org);
  const members = useQuery({ queryKey: ['org-members', org.org_id], queryFn: () => orgApi.members(org).catch(() => []), staleTime: 60_000 });
  const win = useMemo(() => rangeOf(range, new Date(), custom), [range, custom]);
  const group = action.startsWith('g:') ? (action.slice(2) as AuditGroup) : null;
  const exact = action && !group ? action : '';
  // 觀看類的單項（回放錄影、看即時影像…）：平台的 action 是前綴篩選、拿不到結束那筆 → 不帶 action，跟「全部動作」同一個合併後再篩
  // （第十一輪 B11-01：「回放錄影」看得到、「全部動作」看不到；兩邊要是同一列）
  const watchExact = WATCH_ACTIONS.has(exact);
  const serverAction = exact && !watchExact ? exact : '';
  const q = useInfiniteQuery({
    queryKey: ['org-audit', org.org_id, serverAction, actor, text, device, win.from, win.to],
    queryFn: ({ pageParam }) =>
      orgApi.audit(org, {
        cursor: pageParam || new Date(win.to).toISOString(),
        action: serverAction || undefined,
        actor: actor || undefined,
        q: [device, text].filter(Boolean).join(' ') || undefined,
        // 觀看類單項在前端合併後再篩，一頁拿平台上限 200 筆（第十二輪 B12-10）
        limit: watchExact ? String(WATCH_PAGE_LIMIT) : undefined,
      }),
    initialPageParam: '',
    // 往前翻到範圍起點就停（API 只有「早於」的 cursor）
    getNextPageParam: (last) => {
      const evs = last?.events ?? [];
      if (!last?.next_cursor || !evs.length) return undefined;
      return Date.parse(evs[evs.length - 1].created_at) < win.from ? undefined : last.next_cursor;
    },
  });
  const only = group ?? (watchExact ? 'media' : null);
  const events = (q.data?.pages.flatMap((p) => p?.events ?? []) ?? []).filter((e) => Date.parse(e.created_at) >= win.from && (!only || auditGroupOf(e.action) === only));
  const match = useMemo(() => (watchExact ? (row: AuditRowData) => rowMatchesAction(row, exact) : undefined), [watchExact, exact]);
  return (
    <section className="card">
      <div className="toolbar wrap">
        <div className="seg" role="group" aria-label={t('audit.range')}>
          {(['today', 'yesterday', '7d', 'custom'] as const).map((r) => (
            <button key={r} type="button" aria-pressed={range === r} className={range === r ? 'active' : undefined} onClick={() => setRange(r)}>
              {t('audit.r_' + r)}
            </button>
          ))}
        </div>
        {range === 'custom' && (
          <>
            <input className="input" type="date" style={{ width: 'auto' }} value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} aria-label={t('audit.from')} />
            <span>–</span>
            <input className="input" type="date" style={{ width: 'auto' }} value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} aria-label={t('audit.to')} />
          </>
        )}
        <select className="input" style={{ width: 'auto' }} value={action} onChange={(e) => setAction(e.target.value)} aria-label={t('audit.action')}>
          <option value="">{t('audit.all_actions')}</option>
          {grouped.map(({ g, items }) => (
            <optgroup key={g} label={t('audit.g_' + g)}>
              <option value={'g:' + g}>{t('audit.g_all', { group: t('audit.g_' + g) })}</option>
              {items.map((a) => (
                <option key={a} value={a}>
                  {t(actionKey(a))}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {!!members.data?.length && (
          <select className="input" style={{ width: 'auto' }} value={actor} onChange={(e) => setActor(e.target.value)} aria-label={t('audit.actor')}>
            <option value="">{t('audit.all_actors')}</option>
            {members.data.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {memberName(m)}
              </option>
            ))}
          </select>
        )}
        <input
          className="input"
          type="search"
          style={{ width: 'auto', minWidth: 200 }}
          placeholder={t('audit.search')}
          aria-label={t('audit.search')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        {device && (
          <span className="chip" data-testid="audit-device-filter">
            {t('audit.only_device', { name: names.device(device) ?? t('audit.unknown_device') })}{' '}
            <button
              type="button"
              className="icon-btn"
              aria-label={t('audit.clear_device')}
              onClick={() =>
                setSp(
                  (cur) => {
                    const n = new URLSearchParams(cur);
                    n.delete('device');
                    return n;
                  },
                  { replace: true },
                )
              }
            >
              ✕
            </button>
          </span>
        )}
        <span className="spacer" />
        <button className="btn" onClick={() => void exportCsv()}>
          {t('audit.export_csv')}
        </button>
      </div>
      {csvErr && <p className="error small">{t('audit.export_failed')}</p>}
      {/* 換篩選就重新湊一頁（湊滿的計數跟著篩選重來） */}
      <AuditRows key={[action, actor, text, device, win.from, win.to].join('|')} q={q} events={events} names={names} match={match} />
    </section>
  );
}

/** 觀看類篩選一次向平台拿幾筆（平台上限 200）。 */
export const WATCH_PAGE_LIMIT = 200;
/** 篩選後每次湊滿幾列；一次最多自動往前讀幾頁（第十二輪 B12-10） */
export const FILL_ROWS = 20;
export const FILL_MAX_PAGES = 10;

/** 清單（稽核頁與設備詳細「進階 › 稽核」共用；SPEC #vms-web-817）：一筆一列白話摘要，點開看白話欄位，原始 JSON 收在「技術細節」。 */
export function AuditRows({
  q,
  events,
  names,
  limit,
  more,
  match,
}: {
  q: { isLoading: boolean; isError: boolean; hasNextPage?: boolean; isFetchingNextPage?: boolean; fetchNextPage?: () => unknown };
  events: AuditEvent[];
  names: AuditNames;
  /** 最多列幾列（設備頁「最近 20 筆」）；超過或還有下一頁時改放 more 連結 */
  limit?: number;
  /** 〔到稽核頁看全部〕的連結 */
  more?: string;
  /** 合併成列之後再篩（觀看類的動作篩選；第十一輪 B11-01） */
  match?: (row: AuditRowData) => boolean;
}) {
  const { t } = useTranslation();
  // 同一人同一台同一個動作 5 分鐘內合併、一次觀看一列（第二輪 B2-25）
  const all = collapseAudit(events);
  const rows = match ? all.filter(match) : all;
  const shown = limit ? rows.slice(0, limit) : rows;
  const cut = !!limit && (rows.length > limit || !!q.hasNextPage);
  // 篩選後不到一頁（20 列）而且還有下一頁：自動往前讀到湊滿、到範圍起點或沒有下一頁；一次最多 10 頁，
  // 不夠才留〔載入更早的紀錄〕，按一次再湊 20 列（第十二輪 B12-10：「回放錄影」第一頁 2 列、每按一次只多 1 列）
  const [want, setWant] = useState(FILL_ROWS);
  const [burst, setBurst] = useState(0);
  const filling = !!match && !limit && rows.length < want && !!q.hasNextPage && burst < FILL_MAX_PAGES;
  const fetchRef = useRef(q.fetchNextPage);
  fetchRef.current = q.fetchNextPage;
  const busy = !!q.isFetchingNextPage || q.isLoading;
  useEffect(() => {
    if (!filling || busy) return;
    setBurst((b) => b + 1);
    void fetchRef.current?.();
  }, [filling, busy, events.length]);
  const loadMore = () => {
    setWant(rows.length + FILL_ROWS);
    setBurst(0);
    if (!match) void q.fetchNextPage?.();
  };
  return (
    <>
      {q.isLoading && <p className="muted">{t('common.loading')}</p>}
      {q.isError && <p className="error">{t('common.error')}</p>}
      {!q.isLoading && rows.length === 0 && <p className="muted">{t('audit.empty')}</p>}
      <ul className="audit-list" data-testid="audit-list">
        {shown.map(({ e, count, watchedSec, noEnd, members }) => (
          <AuditRow key={e.event_id} e={e} count={count} names={names} watchedSec={watchedSec} noEnd={noEnd} members={members} />
        ))}
      </ul>
      {cut && more && (
        <Link className="btn btn-sm" to={more} data-testid="audit-see-all">
          {t('audit.see_all')}
        </Link>
      )}
      {filling && (
        <p className="muted small" role="status" data-testid="audit-filling">
          {t('audit.filling')}
        </p>
      )}
      {!limit && q.hasNextPage && !filling && (
        <button className="btn" disabled={q.isFetchingNextPage} onClick={loadMore} data-testid="audit-more">
          {t('audit.more')}
        </button>
      )}
    </>
  );
}

function AuditRow({
  e,
  count = 1,
  names,
  watchedSec,
  noEnd,
  members,
}: {
  e: AuditEvent;
  count?: number;
  names: AuditNames;
  watchedSec?: number;
  noEnd?: boolean;
  members?: AuditEvent[];
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const who = e.actor_user_id ? (names.user(e.actor_user_id) ?? t('iam.left_member')) : t('audit.system');
  const fields = open ? auditFields(t, e, names) : [];
  const line = (x: AuditEvent, sec?: number) =>
    sec !== undefined ? auditWatched(t, x, who, names, sec) : noEnd && x.action === 'recording.playback' ? auditReplayStarted(t, x, who, names) : auditSummary(t, x, who, names);
  // 合併列：最新那次的時間＋「× N 次（最早 HH:MM）」；同一分鐘只寫「× N 次」（同警報合併列，第十二輪 B12-01）
  const first = members && members.length > 1 ? members[members.length - 1] : undefined;
  const since = first ? sinceStamp(first.created_at, e.created_at) : '';
  return (
    <li className="audit-row" data-action={e.action}>
      <button type="button" className="audit-main" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="muted small mono">{stamp(e.created_at)}</span>
        <span className="audit-summary">
          {line(e, watchedSec)}
          {/* 配不到結束的「開始看」（舊資料）：註明沒有結束紀錄（第八輪 B8-05） */}
          {noEnd && <span className="muted"> {t('audit.no_end_record')}</span>}
          {count > 1 && (
            <span className="muted" data-testid="audit-times">
              {' '}
              {since ? t('audit.times_since', { n: count, since }) : t('audit.times', { count })}
            </span>
          )}
        </span>
        <span className="chip small">{t('audit.g_' + auditGroupOf(e.action))}</span>
      </button>
      {open && (
        <div className="audit-detail small">
          {/* 合併列逐次列出時間與那一次的摘要（雲端回放寫看的哪一段），第一次不會從畫面上消失（第十二輪 B12-01） */}
          {members && members.length > 1 && (
            <div data-testid="audit-members" style={{ marginBottom: 8 }}>
              <p className="muted">{t('audit.each', { n: members.length })}</p>
              <ul className="plain-list">
                {members.map((x) => (
                  <li key={x.event_id} data-testid="audit-member">
                    <span className="muted mono">{stamp(x.created_at)}</span> · {line(x)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {fields.length > 0 && (
            <dl className="drawer-facts">
              {fields.map((f) => (
                <Fragment key={f.label}>
                  <dt>{f.label}</dt>
                  <dd>{f.value}</dd>
                </Fragment>
              ))}
            </dl>
          )}
          <details>
            <summary>{t('audit.tech')}</summary>
            {/* 合併列列全部 N 筆的原始資料，不只最後一筆 */}
            <pre className="mono small">
              {JSON.stringify(
                members && members.length > 1
                  ? members.map((x) => ({ created_at: x.created_at, action: x.action, resource: x.resource, detail: x.detail, home_id: x.home_id }))
                  : { action: e.action, resource: e.resource, detail: e.detail, home_id: e.home_id },
                null,
                2,
              )}
            </pre>
          </details>
        </div>
      )}
    </li>
  );
}
